/**
 * A key's code in the permissions claim: its 32-bit FNV-1a hash as 5 base-36 characters.
 * `shortKey` must match system2-server's.
 */

/** Length of a generated code. */
const SHORT_KEY_LENGTH = 5;

const CODE_SPACE = 36 ** SHORT_KEY_LENGTH;

/**
 * The code for a page id: 32-bit FNV-1a of the id, as `SHORT_KEY_LENGTH` base-36 characters.
 * @param {string} key - A page id, e.g. `AC_LEAD_DASHBOARD`.
 * @return {string} The code, e.g. `clnz3`.
 */
// Bitwise on purpose: it is a 32-bit hash, and must match the server's integer arithmetic exactly.
/* eslint-disable no-bitwise */
const shortKey = (key) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < key.length; i += 1) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }

  return ((hash >>> 0) % CODE_SPACE).toString(36).padStart(SHORT_KEY_LENGTH, '0');
};
/* eslint-enable no-bitwise */

const hasOwn = (map, key) => Object.prototype.hasOwnProperty.call(map, key);

/**
 * A page's entry in the claim's `permissions`: under its full id (rule-gated keys, short ids, and the
 * later page of two whose codes collide), else under its code.
 * @param {Object<string, string[]>} [permissions] - The claim's `permissions`.
 * @param {string} pageId - e.g. `pages.AC_LEAD_DASHBOARD`.
 * @return {string[]|undefined} The page's levels, or undefined when the caller has none.
 */
const getPagePermissions = (permissions, pageId) => {
  const claim = permissions || {};
  if (hasOwn(claim, pageId)) return claim[pageId];

  const code = shortKey(pageId);
  return hasOwn(claim, code) ? claim[code] : undefined;
};

export {
  getPagePermissions,
  shortKey,
};
